/**
 * A rotary voltage wheel. Twist it (drag around the dial) to raise/lower the
 * coil voltage 0..25 kV; modulating it in rhythm with the swing pumps the balls
 * to resonance. Reports 0..1.
 */
export interface VoltageWheel {
  read(): number;
  readonly el: HTMLElement;
  dispose(): void;
}

const SWEEP = 3.6; // radians of turn for the full 0..1 range

export function createVoltageWheel(parent: HTMLElement): VoltageWheel {
  const wrap = document.createElement('div');
  wrap.className = 'voltage-wheel';
  const dial = document.createElement('div');
  dial.className = 'vw-dial';
  const marker = document.createElement('div');
  marker.className = 'vw-marker';
  dial.appendChild(marker);
  const label = document.createElement('div');
  label.className = 'vw-label';
  label.textContent = '0 kV';
  wrap.appendChild(dial);
  wrap.appendChild(label);
  parent.appendChild(wrap);

  let value = 0;
  let pointerId: number | null = null;
  let prevAngle = 0;

  const angleAt = (clientX: number, clientY: number) => {
    const r = dial.getBoundingClientRect();
    return Math.atan2(clientY - (r.top + r.height / 2), clientX - (r.left + r.width / 2));
  };

  const refresh = () => {
    dial.style.transform = `rotate(${value * SWEEP}rad)`;
    label.textContent = `${Math.round(value * 50)} kV`;
    wrap.classList.toggle('hot', value >= 0.4); // 20 kV — attraction begins
  };

  const onDown = (e: PointerEvent) => {
    pointerId = e.pointerId;
    dial.setPointerCapture(e.pointerId);
    prevAngle = angleAt(e.clientX, e.clientY);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const a = angleAt(e.clientX, e.clientY);
    let d = a - prevAngle;
    if (d > Math.PI) d -= 2 * Math.PI;
    if (d < -Math.PI) d += 2 * Math.PI;
    value = Math.max(0, Math.min(1, value + d / SWEEP));
    prevAngle = a;
    refresh();
    e.preventDefault();
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId === pointerId) pointerId = null;
  };

  dial.addEventListener('pointerdown', onDown);
  dial.addEventListener('pointermove', onMove);
  dial.addEventListener('pointerup', onUp);
  dial.addEventListener('pointercancel', onUp);
  refresh();

  return {
    read: () => value,
    el: wrap,
    dispose() {
      dial.removeEventListener('pointerdown', onDown);
      dial.removeEventListener('pointermove', onMove);
      dial.removeEventListener('pointerup', onUp);
      dial.removeEventListener('pointercancel', onUp);
      wrap.remove();
    },
  };
}
